/**
 * PasscodeGate Component
 *
 * Wraps the app and requires a PIN to access.
 * Features:
 * - 6-digit PIN entry
 * - Backend-based authentication (no secrets in frontend)
 * - HTTP-only session cookies (secure, XSS-resistant)
 * - 5 failed attempts = 5 minute lockout
 * - 6-hour session persistence
 * - Automatic redirect to PIN on session expiration (401)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { CONFIG } from '../config';
import { preloadPreferences } from '../hooks/usePreferences';
import { useSessionStore } from '../store/sessionStore';
import './PasscodeGate.css';

interface PasscodeGateProps {
  children: React.ReactNode;
}

interface PasscodeState {
  isUnlocked: boolean;
  isLoading: boolean;
  failedAttempts: number;
  lockedUntil: number | null;
  sessionRequired: boolean;
  sessionExpiredMessage: boolean;
  backendDown: boolean;
}

const MAX_ATTEMPTS = 5;
const LOCKOUT_DURATION = 5 * 60 * 1000; // 5 minutes

// API base URL - use function to get URL at runtime, not build-time frozen value
const getApiBase = () => CONFIG.getApiBaseUrl();

export function PasscodeGate({ children }: PasscodeGateProps): JSX.Element {
  const [state, setState] = useState<PasscodeState>({
    isUnlocked: false,
    isLoading: true,
    failedAttempts: 0,
    lockedUntil: null,
    sessionRequired: true,
    sessionExpiredMessage: false,
    backendDown: false,
  });

  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [isValidating, setIsValidating] = useState(false);

  // Subscribe to session store for session expiration
  const sessionExpiredAt = useSessionStore((s) => s.sessionExpiredAt);
  const setSessionAuthenticated = useSessionStore((s) => s.setAuthenticated);

  // Check session status on mount
  useEffect(() => {
    checkSessionStatus();
  }, []);

  // Listen for session expiration from API calls (401 responses)
  useEffect(() => {
    if (sessionExpiredAt && state.isUnlocked) {
      // Session expired while app was running - show PIN form with message
      setState(prev => ({
        ...prev,
        isUnlocked: false,
        sessionExpiredMessage: true,
      }));
      setPin('');
      setError('');
    }
  }, [sessionExpiredAt, state.isUnlocked]);

  // Check if we have a valid session
  const checkSessionStatus = async () => {
    console.log('[PasscodeGate] Checking session status');
    try {
      const response = await fetch(`${getApiBase()}/auth/pin/status`, {
        credentials: 'include', // Include cookies
      });

      if (!response.ok) {
        throw new Error('Failed to check session status');
      }

      const data = await response.json();

      // If already authenticated, preload preferences BEFORE unlocking
      // This ensures components get positions immediately when they mount
      if (data.authenticated) {
        try {
          await preloadPreferences();
        } catch (err) {
          console.error('[PasscodeGate] Failed to preload preferences:', err);
          // Continue anyway - preferences will load lazily
        }
      }

      // Sync with session store
      setSessionAuthenticated(data.authenticated);
      console.log('[PasscodeGate] Session status resolved', {
        authenticated: data.authenticated,
        sessionRequired: data.sessionRequired,
      });

      setState(prev => ({
        ...prev,
        isUnlocked: data.authenticated,
        isLoading: false,
        sessionRequired: data.sessionRequired,
        sessionExpiredMessage: false,
      }));
    } catch (err) {
      console.error('[PasscodeGate] Session check failed:', err);
      // Check if it's a network error (backend down) vs other error
      const isNetworkError = err instanceof TypeError && err.message.includes('fetch');
      setState(prev => ({
        ...prev,
        isLoading: false,
        sessionRequired: true,
        isUnlocked: false,
        backendDown: isNetworkError || (err instanceof Error && err.message.includes('Failed to fetch')),
      }));
    }
  };

  // Check lockout timer
  useEffect(() => {
    if (state.lockedUntil && state.lockedUntil > Date.now()) {
      const timer = setInterval(() => {
        if (Date.now() >= state.lockedUntil!) {
          setState(prev => ({ ...prev, lockedUntil: null, failedAttempts: 0 }));
          setError('');
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [state.lockedUntil]);

  const validatePasscode = useCallback(
    async (enteredPin: string) => {
      console.log('[PasscodeGate] Validating PIN');
      if (isValidating) return;

      // Check lockout
      if (state.lockedUntil && state.lockedUntil > Date.now()) {
        return;
      }

      setIsValidating(true);
      setError('');

      try {
        const response = await fetch(`${getApiBase()}/auth/pin/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include', // Include cookies (server sets session cookie)
          body: JSON.stringify({ pin: enteredPin }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          console.log('[PasscodeGate] PIN validation succeeded');
          // Success - session cookie is set by server
          // Preload preferences BEFORE unlocking so components get positions immediately
          try {
            await preloadPreferences();
          } catch (err) {
            console.error('[PasscodeGate] Failed to preload preferences:', err);
            // Continue anyway - preferences will load lazily
          }

          // Sync with session store
          setSessionAuthenticated(true);

          setState(prev => ({
            ...prev,
            isUnlocked: true,
            failedAttempts: 0,
            lockedUntil: null,
            sessionExpiredMessage: false,
          }));
        } else {
          console.warn('[PasscodeGate] PIN validation failed', { status: response.status });
          // Failed
          const attempts = state.failedAttempts + 1;
          if (attempts >= MAX_ATTEMPTS) {
            const lockedUntil = Date.now() + LOCKOUT_DURATION;
            setState(prev => ({
              ...prev,
              failedAttempts: attempts,
              lockedUntil,
            }));
            setError(`Too many attempts. Locked for 5 minutes.`);
          } else {
            setState(prev => ({ ...prev, failedAttempts: attempts }));
            setError(`Incorrect PIN. ${MAX_ATTEMPTS - attempts} attempts remaining.`);
          }
          setPin('');
        }
      } catch (err) {
        console.error('[PasscodeGate] Validation error:', err);
        setError('Connection error. Please try again.');
        setPin('');
      } finally {
        setIsValidating(false);
      }
    },
    [state.failedAttempts, state.lockedUntil, isValidating]
  );

  const handlePinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
    setError('');

    // Auto-submit when 6 digits entered
    if (value.length === 6) {
      validatePasscode(value);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length === 6) {
      validatePasscode(pin);
    }
  };

  const handleKeypadClick = (digit: string) => {
    if (pin.length < 6) {
      const newPin = pin + digit;
      setPin(newPin);
      setError('');
      if (newPin.length === 6) {
        validatePasscode(newPin);
      }
    }
  };

  const handleBackspace = () => {
    setPin(prev => prev.slice(0, -1));
    setError('');
  };

  // Retry checking backend connection
  const retryConnection = useCallback(() => {
    setState(prev => ({ ...prev, isLoading: true, backendDown: false }));
    checkSessionStatus();
  }, []);

  // Show loading while checking session
  if (state.isLoading) {
    return (
      <div className="passcode-gate">
        <div className="passcode-container">
          <div className="passcode-logo">
            <span className="passcode-logo-icon">📈</span>
            <h1>Trading Terminal</h1>
          </div>
          <p className="passcode-prompt">Checking session...</p>
        </div>
      </div>
    );
  }

  // Show maintenance page when backend is down
  if (state.backendDown) {
    return (
      <div className="passcode-gate maintenance-mode">
        <div className="passcode-container maintenance-container">
          <div className="maintenance-icon">🔧</div>
          <h1>Down for Maintenance</h1>
          <p className="maintenance-message">
            The trading terminal is currently undergoing maintenance.
            <br />
            Please try again in a few minutes.
          </p>
          <button
            className="maintenance-retry-btn"
            onClick={retryConnection}
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // If session not required (PIN auth not configured on server), skip gate
  if (!state.sessionRequired) {
    return <>{children}</>;
  }

  // If unlocked, render children
  if (state.isUnlocked) {
    return <>{children}</>;
  }

  // Calculate remaining lockout time
  const remainingLockout = state.lockedUntil
    ? Math.max(0, Math.ceil((state.lockedUntil - Date.now()) / 1000))
    : 0;

  const isLocked = remainingLockout > 0;

  return (
    <div className="passcode-gate">
      <div className="passcode-container">
        <div className="passcode-logo">
          <span className="passcode-logo-icon">📈</span>
          <h1>Trading Terminal</h1>
        </div>

        <form onSubmit={handleSubmit} className="passcode-form">
          {state.sessionExpiredMessage ? (
            <p className="passcode-prompt session-expired">Session expired. Please re-enter PIN.</p>
          ) : (
            <p className="passcode-prompt">Enter PIN to continue</p>
          )}

          {/* PIN dots display */}
          <div className="passcode-dots">
            {[0, 1, 2, 3, 4, 5].map(i => (
              <div
                key={i}
                className={`passcode-dot ${i < pin.length ? 'filled' : ''} ${error ? 'error' : ''}`}
              />
            ))}
          </div>

          {/* Hidden input for keyboard entry */}
          <input
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            value={pin}
            onChange={handlePinChange}
            className="passcode-input"
            autoFocus
            disabled={isLocked || isValidating}
            maxLength={6}
          />

          {/* Error message */}
          {error && <p className="passcode-error">{error}</p>}

          {/* Lockout countdown */}
          {isLocked && (
            <p className="passcode-lockout">
              Try again in {Math.floor(remainingLockout / 60)}:
              {(remainingLockout % 60).toString().padStart(2, '0')}
            </p>
          )}

          {/* Numeric keypad for mobile */}
          <div className="passcode-keypad">
            {['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', '⌫'].map((key, i) => (
              <button
                key={i}
                type="button"
                className={`keypad-btn ${key === '' ? 'empty' : ''} ${key === '⌫' ? 'backspace' : ''}`}
                onClick={() => {
                  if (key === '⌫') handleBackspace();
                  else if (key) handleKeypadClick(key);
                }}
                disabled={isLocked || isValidating || key === ''}
              >
                {key}
              </button>
            ))}
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * Logout function - call this to clear the session
 */
export async function logout(): Promise<boolean> {
  try {
    const response = await fetch(`${getApiBase()}/auth/pin/logout`, {
      method: 'POST',
      credentials: 'include',
    });

    if (response.ok) {
      // Reload the page to show the PIN gate again
      window.location.reload();
      return true;
    }
    return false;
  } catch (err) {
    console.error('[PasscodeGate] Logout failed:', err);
    return false;
  }
}

export default PasscodeGate;
